<script>
import bem from '@/utils/bem';
import PencilIcon from '@/components/svg/PencilIcon/PencilIcon.vue';
import EditTitleWithLinesModal from '@/components/pageContent/modules/EditTitleWithLinesModal.vue';
import EditTitleModal from '@/components/pageContent/modules/EditTitleModal.vue';
import EditDescriptionModal from '@/components/pageContent/modules/EditDescriptionModal.vue';
import EditButtonModal from '@/components/pageContent/modules/EditButtonModal.vue';
import EditPriceModal from '@/components/pageContent/modules/EditPriceModal.vue';
import ImageUploadModal from '@/components/pageContent/modules/ImageUploadModal.vue';
import publishGuardMixin from '@/mixins/publishGuardMixin';
import TitleWithLinesBlock from './title-with-lines-block.vue';
import styles from './big-image-with-tabs-block-style.scss';

const b = bem('big-image-with-tabs-block', styles);

export default {
  mixins: [publishGuardMixin],
  name: 'BigImageWithTabsBlock',
  components: {
    PencilIcon,
    EditTitleWithLinesModal,
    EditTitleModal,
    EditDescriptionModal,
    EditButtonModal,
    EditPriceModal,
    ImageUploadModal,
    TitleWithLinesBlock,
  },
  props: {
    // Title is shared for all tabs - not inside tabs array
    titlesList: {
      type: Array,
      default: () => [],
    },
    tabs: {
      type: Array,
      default: () => [],
    },
    isMobileView: {
      type: Boolean,
      default: false,
    },
    isEditMode: {
      type: Boolean,
      default: false,
    },
    currentPage: {
      type: String,
      default: '',
    },
  },
  emits: ['update', 'update-title'],
  data() {
    return {
      selectedTabIndex: 0,
      // Modals
      titleModalVisible: false,
      tabTitleModalVisible: false,
      editingTabIndex: null,
      subtitleModalVisible: false,
      descriptionModalVisible: false,
      buttonModalVisible: false,
      priceModalVisible: false,
      imageModalVisible: false,
    };
  },
  computed: {
    b() {
      return b;
    },
    // Current tab from tabs array
    currentTab() {
      return this.tabs[this.selectedTabIndex] || {};
    },
    // Title - shared for all tabs
    hasTitle() {
      return this.titlesList.length > 0;
    },
    // Image - fallback to 'mediaData' for legacy data
    imageSrc() {
      if (this.isMobileView) {
        return this.currentTab.imageSrcMobile || this.currentTab.imageSrc || this.currentTab.mediaData || '';
      }
      return this.currentTab.imageSrc || this.currentTab.mediaData || '';
    },
    imageText() {
      if (this.isMobileView) {
        return this.currentTab.imageTextMobile || this.currentTab.imageText || '';
      }
      return this.currentTab.imageText || '';
    },
    hasImageText() {
      return !!this.currentTab.imageText;
    },
    // Video
    isVideo() {
      return this.currentTab.isVideo || false;
    },
    videoSrc() {
      if (this.isMobileView) {
        return this.currentTab.videoSrcMobile || this.currentTab.videoSrc || '';
      }
      return this.currentTab.videoSrc || '';
    },
    // Subtitle (optional) - fallback to 'title' for legacy data
    subtitle() {
      return this.currentTab.subtitle || this.currentTab.title || '';
    },
    subtitleMobile() {
      return this.currentTab.subtitleMobile || this.currentTab.titleMobile || this.subtitle;
    },
    displaySubtitle() {
      return this.isMobileView ? this.subtitleMobile : this.subtitle;
    },
    hasSubtitle() {
      return !!this.subtitle;
    },
    // Description (optional)
    description() {
      return this.currentTab.description || '';
    },
    descriptionMobile() {
      return this.currentTab.descriptionMobile || this.description;
    },
    displayDescription() {
      return this.isMobileView ? this.descriptionMobile : this.description;
    },
    hasDescription() {
      return !!this.description;
    },
    // Price (optional) - same structure as header-block
    withPrice() {
      return this.currentTab.withPrice || false;
    },
    price() {
      return this.currentTab.price || 0;
    },
    additionalDiscount() {
      return this.currentTab.additionalDiscount || 0;
    },
    beforeText() {
      return this.currentTab.beforeText || '';
    },
    afterText() {
      return this.currentTab.afterText || this.currentTab.perText || '';
    },
    productId() {
      return this.currentTab.productId || 0;
    },
    optionId() {
      return this.currentTab.optionId || 0;
    },
    hasPrice() {
      return this.price > 0 || this.productId || this.optionId;
    },
    // Button (optional)
    buttonText() {
      return this.currentTab.buttonText || '';
    },
    buttonTextMobile() {
      return this.currentTab.buttonTextMobile || this.buttonText;
    },
    displayButtonText() {
      return this.isMobileView ? this.buttonTextMobile : this.buttonText;
    },
    buttonLink() {
      return this.currentTab.buttonLink || '';
    },
    hasButton() {
      return !!this.buttonText;
    },
    // Editing tab computed helpers
    editingTab() {
      if (this.editingTabIndex === null) return null;
      return this.tabs[this.editingTabIndex] || null;
    },
    editingTabTitle() {
      return this.editingTab ? this.editingTab.tabTitle : '';
    },
    editingTabTitleMobile() {
      if (!this.editingTab) return '';
      return this.editingTab.tabTitleMobile || this.editingTab.tabTitleMob || '';
    },
  },
  methods: {
    // BEM helper
    bemClass(element, modifiers) {
      return b(element, modifiers);
    },
    // Get tab title based on view mode
    getTabTitle(tab) {
      if (this.isMobileView) {
        return tab.tabTitleMobile || tab.tabTitleMob || tab.tabTitle;
      }
      return tab.tabTitle;
    },
    // Tab selection
    selectTab(index) {
      this.selectedTabIndex = index;
    },
    // Tab click handler - separate actions for pencil and tab body
    handleTabClick(index) {
      if (!this.isEditMode) {
        this.selectTab(index);
      } else {
        // In edit mode, clicking tab body still switches tab
        this.selectTab(index);
      }
    },
    handleTabPencilClick(index, event) {
      event.stopPropagation();
      if (this.isEditMode) {
        this.editingTabIndex = index;
        this.tabTitleModalVisible = true;
      }
    },
    // Modal openers
    openTitleModal() {
      if (!this.guardPublished()) return;
      if (this.isEditMode) {
        this.titleModalVisible = true;
      }
    },
    openSubtitleModal() {
      if (!this.guardPublished()) return;
      if (this.isEditMode) {
        this.subtitleModalVisible = true;
      }
    },
    openDescriptionModal() {
      if (!this.guardPublished()) return;
      if (this.isEditMode) {
        this.descriptionModalVisible = true;
      }
    },
    openButtonModal() {
      if (!this.guardPublished()) return;
      if (this.isEditMode) {
        this.buttonModalVisible = true;
      }
    },
    openPriceModal() {
      if (!this.guardPublished()) return;
      if (this.isEditMode) {
        this.priceModalVisible = true;
      }
    },
    openImageModal() {
      if (!this.guardPublished()) return;
      if (this.isEditMode) {
        this.imageModalVisible = true;
      }
    },
    // Mobile view helpers - get image/video source for specific tab
    getTabImageSrc(tab) {
      return tab.imageSrcMobile || tab.imageSrc || tab.mediaData?.srcMobile || tab.mediaData?.src || tab.mediaData || '';
    },
    getTabVideoSrc(tab) {
      return tab.videoSrcMobile || tab.videoSrc || '';
    },
    // Mobile view modal openers for specific tabs
    openImageModalForTab(index) {
      if (!this.guardPublished()) return;
      if (this.isEditMode) {
        this.selectedTabIndex = index;
        this.imageModalVisible = true;
      }
    },
    openSubtitleModalForTab(index) {
      if (!this.guardPublished()) return;
      if (this.isEditMode) {
        this.selectedTabIndex = index;
        this.subtitleModalVisible = true;
      }
    },
    openDescriptionModalForTab(index) {
      if (!this.guardPublished()) return;
      if (this.isEditMode) {
        this.selectedTabIndex = index;
        this.descriptionModalVisible = true;
      }
    },
    openButtonModalForTab(index) {
      if (!this.guardPublished()) return;
      if (this.isEditMode) {
        this.selectedTabIndex = index;
        this.buttonModalVisible = true;
      }
    },
    openPriceModalForTab(index) {
      if (!this.guardPublished()) return;
      if (this.isEditMode) {
        this.selectedTabIndex = index;
        this.priceModalVisible = true;
      }
    },
    // Save handlers
    handleTitleSave(values) {
      this.$emit('update-title', values);
      this.titleModalVisible = false;
    },
    handleTitleDelete() {
      this.$emit('update-title', []);
      this.titleModalVisible = false;
    },
    handleTabTitleSave(values) {
      const updatedTabs = [...this.tabs];
      if (this.editingTabIndex !== null && updatedTabs[this.editingTabIndex]) {
        updatedTabs[this.editingTabIndex] = {
          ...updatedTabs[this.editingTabIndex],
          tabTitle: values.title,
          tabTitleMobile: values.titleMobile,
        };
        this.$emit('update', updatedTabs);
      }
      this.tabTitleModalVisible = false;
      this.editingTabIndex = null;
    },
    handleTabTitleDelete() {
      const updatedTabs = [...this.tabs];
      if (this.editingTabIndex !== null && updatedTabs[this.editingTabIndex]) {
        updatedTabs[this.editingTabIndex] = {
          ...updatedTabs[this.editingTabIndex],
          tabTitle: '',
          tabTitleMobile: '',
        };
        this.$emit('update', updatedTabs);
      }
      this.tabTitleModalVisible = false;
      this.editingTabIndex = null;
    },
    handleSubtitleSave(values) {
      this.updateCurrentTab({
        subtitle: values.title,
        subtitleMobile: values.titleMobile,
      });
      this.subtitleModalVisible = false;
    },
    handleSubtitleDelete() {
      this.updateCurrentTab({ subtitle: '', subtitleMobile: '' });
      this.subtitleModalVisible = false;
    },
    handleDescriptionSave(values) {
      this.updateCurrentTab({
        description: values.description,
        descriptionMobile: values.descriptionMobile,
      });
      this.descriptionModalVisible = false;
    },
    handleDescriptionDelete() {
      this.updateCurrentTab({ description: '', descriptionMobile: '' });
      this.descriptionModalVisible = false;
    },
    handleButtonSave(values) {
      this.updateCurrentTab({
        buttonText: values.buttonText,
        buttonTextMobile: values.buttonTextMobile,
        buttonLink: values.buttonLink,
      });
      this.buttonModalVisible = false;
    },
    handleButtonDelete() {
      this.updateCurrentTab({
        buttonText: '',
        buttonTextMobile: '',
        buttonLink: '',
      });
      this.buttonModalVisible = false;
    },
    handlePriceSave(values) {
      this.updateCurrentTab({
        beforeText: values.beforeText,
        afterText: values.afterText,
        productId: values.productId,
        optionId: values.optionId,
        price: values.price,
        additionalDiscount: values.additionalDiscount,
        withPrice: true,
      });
      this.priceModalVisible = false;
    },
    handlePriceDelete() {
      this.updateCurrentTab({
        beforeText: '',
        afterText: '',
        productId: 0,
        optionId: 0,
        price: 0,
        additionalDiscount: 0,
        withPrice: false,
      });
      this.priceModalVisible = false;
    },
    handleImageSave(values) {
      this.updateCurrentTab({
        imageSrc: values.image,
        imageSrcMobile: values.imageMobile,
        imageText: values.imageText,
        imageTextMobile: values.imageTextMobile,
      });
      this.imageModalVisible = false;
    },
    handleImageDelete() {
      this.updateCurrentTab({
        imageSrc: '',
        imageSrcMobile: '',
        imageText: '',
        imageTextMobile: '',
      });
      this.imageModalVisible = false;
    },
    // Helper to update current tab
    updateCurrentTab(updates) {
      const updatedTabs = [...this.tabs];
      if (updatedTabs[this.selectedTabIndex]) {
        updatedTabs[this.selectedTabIndex] = {
          ...updatedTabs[this.selectedTabIndex],
          ...updates,
        };
        this.$emit('update', updatedTabs);
      }
    },
  },
};
</script>

<template>
  <article :class="b({ isMobileView, isEditMode })">
    <!-- ==================== DESKTOP VIEW ==================== -->
    <template v-if="!isMobileView">
      <!-- Title (TitleWithLinesBlock) - shared for all tabs -->
      <div
        v-if="hasTitle || isEditMode"
        :class="b('title-wrap', { editable: isEditMode })"
        role="button"
        tabindex="0"
        @click="openTitleModal"
        @keyup.enter="openTitleModal"
      >
        <TitleWithLinesBlock
          v-if="hasTitle"
          :class="b('title')"
          :is-mobile-view="false"
          :text-list="titlesList"
        />
        <div v-else-if="isEditMode" :class="b('add-field')">
          + Add title
        </div>
        <PencilIcon
          v-if="isEditMode && hasTitle"
          :class="b('edit-icon')"
          color="#fff"
          :size="20"
        />
      </div>

      <!-- Image/Video block -->
      <div :class="b('media-wrap')">
        <div
          :class="b('media-block', { editable: isEditMode })"
          role="button"
          tabindex="0"
          @click="openImageModal"
          @keyup.enter="openImageModal"
        >
          <!-- Video -->
          <video
            v-if="isVideo && videoSrc"
            :class="b('media')"
            :src="videoSrc"
            autoplay
            muted
            loop
            playsinline
          />
          <!-- Image -->
          <div
            v-else-if="imageSrc"
            :class="b('media')"
            :style="{
              backgroundImage: `url(${imageSrc})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }"
          />
          <!-- Placeholder -->
          <div v-else :class="b('media-placeholder')">
            <template v-if="isEditMode">
              <span :class="b('placeholder-plus')">+</span><br>
              CLICK TO UPLOAD
            </template>
          </div>
        </div>
        <!-- Image text -->
        <p v-if="hasImageText" :class="b('image-text')">
          {{ imageText }}
        </p>
      </div>

      <!-- Tabs -->
      <div v-if="tabs.length > 1" :class="b('tabs-wrap')">
        <button
          v-for="(tab, index) in tabs"
          :key="tab.id || index"
          type="button"
          :class="b('tab', { active: selectedTabIndex === index })"
          @click="handleTabClick(index)"
        >
          <span :class="b('tab-text')">
            {{ getTabTitle(tab) }}
          </span>
          <PencilIcon
            v-if="isEditMode"
            :class="b('tab-edit-icon')"
            :color="selectedTabIndex === index ? '#fff' : '#5f8aa4'"
            :size="14"
            @click="handleTabPencilClick(index, $event)"
          />
        </button>
      </div>

      <!-- Subtitle (optional) -->
      <div
        v-if="hasSubtitle || isEditMode"
        :class="b('subtitle-wrap', { editable: isEditMode })"
        role="button"
        tabindex="0"
        @click="openSubtitleModal"
        @keyup.enter="openSubtitleModal"
      >
        <h2 v-if="hasSubtitle" :class="b('subtitle')">
          {{ displaySubtitle }}
        </h2>
        <div v-else-if="isEditMode" :class="b('add-field')">
          + Add subtitle
        </div>
        <PencilIcon
          v-if="isEditMode && hasSubtitle"
          :class="b('edit-icon')"
          color="#fff"
          :size="18"
        />
      </div>

      <!-- Description (optional) -->
      <div
        v-if="hasDescription || isEditMode"
        :class="b('description-wrap', { editable: isEditMode })"
        role="button"
        tabindex="0"
        @click="openDescriptionModal"
        @keyup.enter="openDescriptionModal"
      >
        <p v-if="hasDescription" :class="b('description')">
          {{ displayDescription }}
        </p>
        <div v-else-if="isEditMode" :class="b('add-field')">
          + Add description
        </div>
        <PencilIcon
          v-if="isEditMode && hasDescription"
          :class="b('edit-icon')"
          color="#fff"
          :size="18"
        />
      </div>

      <!-- Price (optional) -->
      <div
        v-if="hasPrice"
        :class="b('price-wrap', { editable: isEditMode })"
        role="button"
        tabindex="0"
        @click="openPriceModal"
        @keyup.enter="openPriceModal"
      >
        <div :class="b('price-block')">
          <span v-if="beforeText" :class="b('price-before')">{{ beforeText }}</span>
          <template v-if="additionalDiscount">
            <span :class="b('price-old')">${{ price }}</span>
            <span :class="b('price-value')">${{ price - additionalDiscount }}</span>
          </template>
          <span v-else :class="b('price-value')">${{ price }}</span>
          <span v-if="afterText" :class="b('price-after')">{{ afterText }}</span>
        </div>
        <PencilIcon
          v-if="isEditMode"
          :class="b('edit-icon')"
          color="#fff"
          :size="16"
        />
      </div>
      <div
        v-else-if="isEditMode"
        :class="b('add-field')"
        role="button"
        tabindex="0"
        @click="openPriceModal"
        @keyup.enter="openPriceModal"
      >
        + Add price with product
      </div>

      <!-- Button (optional) -->
      <div
        v-if="hasButton || isEditMode"
        :class="b('button-wrap', { editable: isEditMode })"
        role="button"
        tabindex="0"
        @click="openButtonModal"
        @keyup.enter="openButtonModal"
      >
        <div v-if="hasButton" :class="b('button')">
          {{ displayButtonText }}
          <PencilIcon
            v-if="isEditMode"
            :class="b('edit-icon', { inline: true })"
            color="#293241"
            :size="16"
          />
        </div>
        <div v-else-if="isEditMode" :class="b('add-field')">
          + Add button
        </div>
      </div>
    </template>

    <!-- ==================== MOBILE VIEW ==================== -->
    <template v-else>
      <!-- Title (TitleWithLinesBlock) - shared for all tabs -->
      <div
        v-if="hasTitle || isEditMode"
        :class="b('title-wrap', { editable: isEditMode, isMobileView: true })"
        role="button"
        tabindex="0"
        @click="openTitleModal"
        @keyup.enter="openTitleModal"
      >
        <TitleWithLinesBlock
          v-if="hasTitle"
          :class="b('title', { isMobileView: true })"
          :is-mobile-view="true"
          :text-list="titlesList"
        />
        <div v-else-if="isEditMode" :class="b('add-field')">
          + Add title
        </div>
        <PencilIcon
          v-if="isEditMode && hasTitle"
          :class="b('edit-icon')"
          color="#fff"
          :size="20"
        />
      </div>

      <!-- Mobile tabs - all tabs displayed vertically -->
      <div :class="b('mobile-tabs')">
        <div
          v-for="(tab, index) in tabs"
          :key="tab.id || index"
          :class="b('mobile-tab')"
        >
          <div :class="b('media-wrap-block')">
            <!-- Media wrap with image and overlays -->
            <div
              :class="b('media-wrap', { isMobileView: true, editable: isEditMode })"
              role="button"
              tabindex="0"
              @click="openImageModalForTab(index)"
              @keyup.enter="openImageModalForTab(index)"
            >
              <!-- Gradient overlay -->
              <div :class="b('gradient-block')" />

              <!-- Video -->
              <video
                v-if="tab.isVideo && getTabVideoSrc(tab)"
                :class="b('media', { isMobileView: true })"
                :src="getTabVideoSrc(tab)"
                autoplay
                muted
                loop
                playsinline
              />
              <!-- Image -->
              <img
                v-else-if="getTabImageSrc(tab)"
                :class="b('media', { isMobileView: true })"
                :src="getTabImageSrc(tab)"
                alt=""
              />
              <!-- Placeholder -->
              <div v-else :class="b('media-placeholder', { isMobileView: true })">
                <template v-if="isEditMode">
                  <span :class="b('placeholder-plus')">+</span><br>
                  CLICK TO UPLOAD
                </template>
              </div>

              <!-- Tab title on image -->
              <span :class="b('mobile-tab-title')">
                {{ tab.tabTitleMobile || tab.tabTitleMob || tab.tabTitle }}
                <PencilIcon
                  v-if="isEditMode"
                  :class="b('mobile-tab-title-edit')"
                  color="#f4a460"
                  :size="14"
                  @click.stop="handleTabPencilClick(index, $event)"
                />
              </span>

              <!-- Subtitle on image -->
              <h2
                v-if="tab.subtitle || tab.title"
                :class="b('bottom-title')"
                role="button"
                tabindex="0"
                @click.stop="openSubtitleModalForTab(index)"
                @keyup.enter.stop="openSubtitleModalForTab(index)"
              >
                {{ tab.subtitleMobile || tab.titleMobile || tab.subtitle || tab.title }}
                <PencilIcon
                  v-if="isEditMode"
                  :class="b('bottom-title-edit')"
                  color="#fff"
                  :size="12"
                />
              </h2>
              <div
                v-else-if="isEditMode"
                :class="b('bottom-title', { placeholder: true })"
                role="button"
                tabindex="0"
                @click.stop="openSubtitleModalForTab(index)"
                @keyup.enter.stop="openSubtitleModalForTab(index)"
              >
                + Add subtitle
              </div>
            </div>

            <!-- Price and button block -->
            <div :class="b('mobile-tab-price-block', { even: index % 2 !== 0 })">
              <!-- Price -->
              <div
                v-if="tab.withPrice && tab.price"
                :class="b('price-block', {
                  isMobileView: true,
                  even: index % 2 !== 0,
                  editable: isEditMode
                })"
                role="button"
                tabindex="0"
                @click="openPriceModalForTab(index)"
                @keyup.enter="openPriceModalForTab(index)"
              >
                <span
                  v-if="tab.additionalDiscount"
                  :class="b('price-old', { isMobileView: true })"
                >${{ tab.price }}</span>
                <span :class="b('price-value', { isMobileView: true })">
                  ${{ tab.additionalDiscount
                    ? tab.price - tab.additionalDiscount
                    : tab.price }}
                </span>
                <span
                  v-if="tab.afterText || tab.perText"
                  :class="b('price-after', { isMobileView: true })"
                >{{ tab.afterText || tab.perText }}</span>
              </div>
              <div
                v-else-if="isEditMode"
                :class="b('add-field', { mobile: true })"
                role="button"
                tabindex="0"
                @click="openPriceModalForTab(index)"
                @keyup.enter="openPriceModalForTab(index)"
              >
                + Price
              </div>

              <!-- Button -->
              <div
                v-if="tab.buttonText"
                :class="b('button', { isMobileView: true, editable: isEditMode })"
                role="button"
                tabindex="0"
                @click="openButtonModalForTab(index)"
                @keyup.enter="openButtonModalForTab(index)"
              >
                {{ tab.buttonTextMobile || tab.buttonText }}
              </div>
              <div
                v-else-if="isEditMode"
                :class="b('add-field', { mobile: true })"
                role="button"
                tabindex="0"
                @click="openButtonModalForTab(index)"
                @keyup.enter="openButtonModalForTab(index)"
              >
                + Button
              </div>
            </div>
          </div>

          <!-- Description -->
          <div
            v-if="tab.description || isEditMode"
            :class="b('description-wrap', { isMobileView: true, editable: isEditMode })"
            role="button"
            tabindex="0"
            @click="openDescriptionModalForTab(index)"
            @keyup.enter="openDescriptionModalForTab(index)"
          >
            <p v-if="tab.description" :class="b('description', { isMobileView: true })">
              {{ tab.descriptionMobile || tab.description }}
            </p>
            <div v-else-if="isEditMode" :class="b('add-field')">
              + Add description
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- Modals -->
    <!-- Title Modal -->
    <EditTitleWithLinesModal
      :visible="titleModalVisible"
      :data="titlesList"
      :show-delete="titlesList.length > 0"
      @save="handleTitleSave"
      @cancel="titleModalVisible = false"
      @delete="handleTitleDelete"
    />

    <!-- Tab Title Modal -->
    <EditTitleModal
      :visible="tabTitleModalVisible"
      :title="editingTabTitle"
      :title-mobile="editingTabTitleMobile"
      modal-title="Edit Tab Title"
      @close="tabTitleModalVisible = false; editingTabIndex = null"
      @save="handleTabTitleSave"
      @delete="handleTabTitleDelete"
    />

    <!-- Subtitle Modal -->
    <EditTitleModal
      :visible="subtitleModalVisible"
      :title="subtitle"
      :title-mobile="subtitleMobile"
      modal-title="Edit Subtitle"
      @close="subtitleModalVisible = false"
      @save="handleSubtitleSave"
      @delete="handleSubtitleDelete"
    />

    <!-- Description Modal -->
    <EditDescriptionModal
      :visible="descriptionModalVisible"
      :description="description"
      :description-mobile="descriptionMobile"
      @close="descriptionModalVisible = false"
      @save="handleDescriptionSave"
      @delete="handleDescriptionDelete"
    />

    <!-- Button Modal -->
    <EditButtonModal
      :visible="buttonModalVisible"
      :button-text="buttonText"
      :button-text-mobile="buttonTextMobile"
      :button-link="buttonLink"
      @close="buttonModalVisible = false"
      @save="handleButtonSave"
      @delete="handleButtonDelete"
    />

    <!-- Price Modal -->
    <EditPriceModal
      :visible="priceModalVisible"
      :before-text="beforeText"
      :after-text="afterText"
      :product-id="productId"
      :option-id="optionId"
      :show-delete="hasPrice"
      @close="priceModalVisible = false"
      @save="handlePriceSave"
      @delete="handlePriceDelete"
    />

    <!-- Image Modal -->
    <ImageUploadModal
      :visible="imageModalVisible"
      :image="currentTab.imageSrc || currentTab.mediaData || ''"
      :image-mobile="currentTab.imageSrcMobile || ''"
      :image-text="currentTab.imageText || ''"
      :image-text-mobile="currentTab.imageTextMobile || ''"
      :with-image-text="true"
      :show-delete="!!imageSrc"
      @close="imageModalVisible = false"
      @save="handleImageSave"
      @delete="handleImageDelete"
    />
  </article>
</template>
